import React from 'react';
import PillarPageLayout from '../components/PillarPageLayout';

const MultiplyPage: React.FC = () => {
  const DeviceStack = () => (
    <div className="flex flex-col gap-2 items-center">
      {['Laptop', 'Phone', 'Tablet', 'E-reader'].map((d) => (
        <div key={d} className="px-3 py-1.5 rounded-md text-xs font-medium bg-white border border-slate-300 text-slate-600">
          {d}
        </div>
      ))}
    </div>
  );

  const diagram = (
    <>
      <p className="font-semibold text-slate-700 mb-5 text-center text-sm">The Problem</p>
      <div className="flex items-center justify-center gap-3 my-3">
        <DeviceStack />
        <span className="text-slate-400">→</span>
        <div className="px-4 py-2.5 rounded-lg text-sm font-medium bg-amber-100 text-amber-700 border border-amber-300 text-center">
          Hotel WiFi<br />
          <span className="text-xs font-semibold">⚠️ 1 device only</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 text-center italic mt-2">
        WiFi limits you to one device — but you have four
      </p>

      <hr className="border-t border-dashed border-slate-300 my-7" />

      <p className="font-semibold text-slate-700 mb-5 text-center text-sm">With Megadish</p>
      <div className="flex items-center justify-center gap-3 my-3 flex-wrap">
        <DeviceStack />
        <span className="text-slate-400">→</span>
        <div className="px-4 py-2.5 rounded-lg text-sm font-medium bg-green-600 text-white text-center">
          Megadish<br />
          <span className="text-xs">(looks like 1 device)</span>
        </div>
        <span className="text-slate-400">→</span>
        <div className="px-4 py-2.5 rounded-lg text-sm font-medium bg-green-100 text-green-700 border border-green-300 text-center">
          Hotel WiFi<br />
          <span className="text-xs">✓ Sees 1 device</span>
        </div>
      </div>
      <p className="text-xs text-slate-500 text-center italic mt-2">
        WiFi sees Megadish as one device — you get four behind it
      </p>
    </>
  );

  const explanation = (
    <>
      <h3 className="text-xl font-semibold text-slate-800 mb-4">Why multiplying matters</h3>
      <p className="text-slate-600 mb-4 leading-relaxed">
        Many WiFi services limit you to one device — hotels that charge per room, 
        trains with one connection per ticket, expensive airport lounges. 
        But modern life means you carry a phone, laptop, tablet, and maybe more.
      </p>
      <p className="text-slate-600 leading-relaxed">
        Megadish appears as a single device to the public WiFi. Behind it, you can 
        connect up to four of your own devices. The public network only sees one 
        connection, so you stay within their limits while getting full use of your gear.
      </p>
    </>
  );

  const productNote = (
    <>
      <span className="text-xl">💡</span>
      <span>
        <strong>Two-factor authentication:</strong> When your bank sends a code to your phone but you're 
        logging in on your laptop, both devices need to be online simultaneously. With a "one device" 
        limit, that's impossible — unless you use Megadish.
      </span>
    </>
  );

  return (
    <PillarPageLayout
      icon="➕"
      title="Multiply"
      subtitle="Turn one allowed connection into four"
      color="orange"
      diagram={diagram}
      explanation={explanation}
      benefits={[
        'Bypass "one device per room/ticket" limits',
        'Essential for two-factor authentication (phone + laptop must both be online)',
        'Make expensive single-device WiFi passes work harder',
        'Share one paid connection across your devices',
        'Up to 4 devices through one public WiFi slot',
      ]}
      scenarios={[
        { emoji: '🔐', label: 'Banking with 2FA' },
        { emoji: '🏨', label: 'Hotel "1 device" limits' },
        { emoji: '🎫', label: 'Paid WiFi passes' },
        { emoji: '🚂', label: 'Train ticket WiFi' },
      ]}
      productNote={productNote}
    />
  );
};

export default MultiplyPage;
